import {
  Checkbox,
  FormField,
  TextInput,
  Box,
  Typography,
  Tooltip,
  getInputStatus,
} from '@percent/lemonade'
import { useEffect, useRef } from 'react'
import { SelectOptionCard } from '../select-option-card/select-option-card'
import { QuestionBuilder } from './question-builder/question-builder'
import typeStepStyles from '../opportunity-form/type-step/opportunity-form-type-step.module.scss'
import styles from './survey-section.module.scss'
import { SurveySectionProps } from './survey-section.types'
import { SurveyMode } from '../../dtos/survey.dto'
import { getArrayValidation } from '../../utils/validation-utils'
import { hasActiveQuestion } from '../../utils/common-utils'

export function SurveySection({
  hideModePicker,
  allowedQuestionTypes,
  maxQuestions,
  survey,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  getDefaultQuestions,
  fieldNames = {},
  errors,
  touched,
  checkboxLabel,
  checkboxHelperText,
  builtInOptionTitle,
  builtInOptionDescription,
  externalOptionTitle,
  externalOptionDescription,
  externalUrlLabel,
  externalUrlDescription,
  externalUrlPlaceholder,
  questionBuilderHeaderTitle,
  questionBuilderHeaderDescription,
  addQuestionButton,
  defaultRatingMinLabel,
  defaultRatingMaxLabel,
  typeLabel,
  questionLabel,
  requiredLabel,
  questionPreviewLabel,
  selectTypePlaceholder,
  ratingQuestionPlaceholder,
  freeTextQuestionPlaceholder,
  removeButton,
  ratingTypeLabel,
  freeTextTypeLabel,
  activeBadgeLabel,
  deactivatedBadgeLabel,
  questionResponseSummaryByIndex,
  deactivateButtonLabel,
  activateButtonLabel,
  onDeactivateRequest,
  onActivateRequest,
  lastQuestionRemoval = 'deactivate',
  templatePicker,
  readOnly,
  readOnlyTooltip,
}: Readonly<SurveySectionProps>) {
  const isEnabled = survey.surveyEnabled ?? false
  /** When true, questions were cleared via "remove last question" — do not re-seed defaults (see init effect). */
  const clearedByRemovingLastRef = useRef(false)
  /** Previous "had any active question" — only auto-uncheck the survey when we transition from some active to none, not when the user re-enables the section while all questions are still inactive. */
  const hadActiveQuestionPreviouslyRef = useRef<boolean | null>(null)

  // Destructure with defaults to avoid non-null assertions
  const {
    enabled: enabledFieldName = 'surveyEnabled',
    mode: modeFieldName = 'surveyMode',
    externalUrl: externalUrlFieldName = 'surveyExternalUrl',
    questions: questionsFieldName = 'surveyQuestions',
  } = fieldNames

  // Built-in surveys with every question deactivated should not stay "on" (no questions to show).
  // Match QuestionBuilder visibility: pre-event (hideModePicker) or explicit built-in mode — not external or unset mode.
  const isBuiltInUi = Boolean(
    hideModePicker || survey.surveyMode === SurveyMode.BuiltIn
  )

  // Initialize survey defaults when enabled for the first time
  useEffect(() => {
    if (isEnabled) {
      if (!hideModePicker && !survey.surveyMode) {
        setFieldValue(modeFieldName, SurveyMode.BuiltIn)
      }
      if (
        !survey.surveyQuestions?.length &&
        !clearedByRemovingLastRef.current
      ) {
        setFieldValue(
          questionsFieldName,
          getDefaultQuestions ? getDefaultQuestions() : []
        )
      }
    }
  }, [
    isEnabled,
    hideModePicker,
    survey.surveyMode,
    survey.surveyQuestions,
    setFieldValue,
    getDefaultQuestions,
    modeFieldName,
    questionsFieldName,
  ])

  // Uncheck "Ask questions" / post-event when the user deactivates the last active question(s).
  // Do not uncheck when they turn the survey back on with all questions still inactive — that needs to stay
  // on so they can reactivate questions (transition: had at least one active -> now none).
  useEffect(() => {
    if (!isBuiltInUi) return

    const questions = survey.surveyQuestions ?? []
    const anyActive = hasActiveQuestion(questions)

    if (!isEnabled) {
      hadActiveQuestionPreviouslyRef.current = anyActive
      return
    }

    if (questions.length > 0) {
      if (hadActiveQuestionPreviouslyRef.current === true && !anyActive) {
        setFieldValue(enabledFieldName, false)
      }
    }

    hadActiveQuestionPreviouslyRef.current = anyActive
  }, [
    isBuiltInUi,
    isEnabled,
    survey.surveyQuestions,
    setFieldValue,
    enabledFieldName,
  ])

  // Extract touched && errors check for external URL field
  const externalUrlHasError =
    touched?.[externalUrlFieldName] && errors?.[externalUrlFieldName]

  const externalUrlErrorMessage = externalUrlHasError
    ? String(externalUrlHasError)
    : undefined

  const questionsError = errors?.[questionsFieldName]
  const questionsErrorMessage =
    typeof questionsError === 'string' ? questionsError : undefined

  const checkbox = (
    <Checkbox
      name={enabledFieldName}
      value={isEnabled ? 'true' : undefined}
      active={isEnabled}
      onChange={(e) => {
        clearedByRemovingLastRef.current = false
        setFieldValue(enabledFieldName, e.currentTarget.checked)
      }}
      label={checkboxLabel}
      helperText={checkboxHelperText}
      variant="default"
      disabled={readOnly}
    />
  )

  return (
    <Box className={styles.SurveySectionWrapper}>
      {readOnly && readOnlyTooltip ? (
        <Tooltip content={readOnlyTooltip} placement="top-start">
          {/* Native div required — Lemonade Box doesn't forwardRef, so Tooltip can't attach its ref */}
          <div className={styles.TooltipTrigger}>{checkbox}</div>
        </Tooltip>
      ) : (
        checkbox
      )}

      {isEnabled && !readOnly && (
        <>
          {!hideModePicker && (
            <Box className={typeStepStyles.OptionsCardsBox}>
              <SelectOptionCard
                testId="survey-type-builtin"
                title={builtInOptionTitle}
                description={builtInOptionDescription}
                active={survey.surveyMode === SurveyMode.BuiltIn}
                onClick={() => {
                  setFieldValue(modeFieldName, SurveyMode.BuiltIn)
                }}
              />
              <SelectOptionCard
                testId="survey-type-external"
                title={externalOptionTitle}
                description={externalOptionDescription}
                active={survey.surveyMode === SurveyMode.ExternalLink}
                onClick={() => {
                  setFieldValue(modeFieldName, SurveyMode.ExternalLink)
                }}
              />
            </Box>
          )}

          {templatePicker}

          {!hideModePicker && survey.surveyMode === SurveyMode.ExternalLink && (
            <FormField
              label={externalUrlLabel}
              necessity="required"
              description={externalUrlDescription}
              status={getInputStatus(externalUrlErrorMessage)}
              statusMessage={externalUrlErrorMessage}
            >
              <TextInput
                name={externalUrlFieldName}
                value={survey.surveyExternalUrl || ''}
                onChange={(e) =>
                  setFieldValue(externalUrlFieldName, e.target.value)
                }
                onBlur={handleBlur}
                placeholder={externalUrlPlaceholder}
              />
            </FormField>
          )}

          {(hideModePicker || survey.surveyMode === SurveyMode.BuiltIn) && (
            <Box>
              <QuestionBuilder
                questions={survey.surveyQuestions || []}
                onChange={(questions) => {
                  setFieldValue(questionsFieldName, questions)
                  // Only the "clear" last-question path yields []; built-in + enabled are already required for this subtree.
                  if (questions.length > 0) {
                    return
                  }
                  clearedByRemovingLastRef.current = true
                  setFieldValue(enabledFieldName, false)
                }}
                errors={getArrayValidation(errors?.[questionsFieldName])}
                touched={getArrayValidation(touched?.[questionsFieldName])}
                setFieldTouched={setFieldTouched}
                headerTitle={questionBuilderHeaderTitle}
                headerDescription={questionBuilderHeaderDescription}
                addQuestionButton={addQuestionButton}
                defaultRatingMinLabel={defaultRatingMinLabel}
                defaultRatingMaxLabel={defaultRatingMaxLabel}
                typeLabel={typeLabel}
                questionLabel={questionLabel}
                requiredLabel={requiredLabel}
                questionPreviewLabel={questionPreviewLabel}
                selectTypePlaceholder={selectTypePlaceholder}
                ratingQuestionPlaceholder={ratingQuestionPlaceholder}
                freeTextQuestionPlaceholder={freeTextQuestionPlaceholder}
                removeButton={removeButton}
                ratingTypeLabel={ratingTypeLabel}
                freeTextTypeLabel={freeTextTypeLabel}
                activeBadgeLabel={activeBadgeLabel}
                deactivatedBadgeLabel={deactivatedBadgeLabel}
                questionResponseSummaryByIndex={questionResponseSummaryByIndex}
                deactivateButtonLabel={deactivateButtonLabel}
                activateButtonLabel={activateButtonLabel}
                allowedQuestionTypes={allowedQuestionTypes}
                questionsFieldName={questionsFieldName}
                maxQuestions={maxQuestions}
                onDeactivateRequest={onDeactivateRequest}
                onActivateRequest={onActivateRequest}
                lastQuestionRemoval={lastQuestionRemoval}
              />

              {questionsErrorMessage && (
                <Typography variant="small" $color={'alert400'}>
                  {questionsErrorMessage}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
